import React, { useEffect, useState } from 'react';
import {
  IEtf,
  IEtfValue,
  useGetEtfLazyQuery,
  useGetEtfValuesQuery,
  useEtfValueCreatedSubscription,
  useEtfValueUpdatedSubscription
} from '../../generated/graphql';
import OverviewTable from '../Common/Tables/OverviewTable';
import { useLocation, useNavigate } from 'react-router-dom';
import OverviewToolbar from '../Common/Layout/OverviewToolbar';
import { AuthRoutes, RouteNames } from '../../enums/RouteEnums';
import { renderDateString } from '../../utils/styleUtils';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment-timezone';
import { DATE_TIME_FORMATS } from '../../utils/constants';
import { exportDataAsExcel } from '../../utils/fileUtiles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import EtfValueForm from './EtfValueForm';

interface LocationState {
  name: string;
}

const minDate = new Date(2000, 0, 1);

const EtfHistory: React.FunctionComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const locationElements = location.pathname.split('/').filter((e) => e);
  const [etfValues, setEtfValues] = useState<IEtfValue[]>([]);
  const { data, loading } = useGetEtfValuesQuery({
    variables: {
      etf_id: parseInt(locationElements[locationElements.length - 1])
    },
    fetchPolicy: 'no-cache'
  });
  const [getEtf, etfData] = useGetEtfLazyQuery();
  const [etf, setEtf] = useState<IEtf | null>((location.state as IEtf) || null);
  const [etfValue, setEtfValue] = useState<IEtfValue | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [filteredData, setFilteredData] = useState<IEtfValue[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const createEtfValueSubscription = useEtfValueCreatedSubscription({
    variables: { etf_id: etf?.id || '' }
  });
  const updateEtfValueSubscription = useEtfValueUpdatedSubscription({
    variables: { etf_id: etf?.id || '' }
  });

  useEffect(() => {
    if (!etf) {
      getEtf({ variables: { id: locationElements[locationElements.length - 1] } });
    }
  }, []);

  useEffect(() => {
    if (createEtfValueSubscription.data && createEtfValueSubscription.data?.result) {
      addEtfValueDocument(createEtfValueSubscription.data.result as IEtfValue);
    }
    if (updateEtfValueSubscription.data && updateEtfValueSubscription.data?.result) {
      const updated = updateEtfValueSubscription.data?.result as IEtfValue;
      setEtfValues(etfValues.map((v) => (v.id === updated.id ? updated : v)));
      let includeInFiltered = true;
      const currentStartDate = startDate
        ? moment.utc(new Date(startDate.toISOString().split('T')[0]))
        : null;
      const currentEndDate = endDate
        ? moment.utc(new Date(endDate.toISOString().split('T')[0]))
        : null;
      if (currentStartDate) {
        includeInFiltered = moment.utc(updated.date).isSameOrAfter(moment(currentStartDate));
      }
      if (includeInFiltered && currentEndDate) {
        includeInFiltered = moment.utc(updated.date).isSameOrBefore(currentEndDate);
      }
      if (includeInFiltered) {
        setFilteredData(filteredData.map((v) => (v.id === updated.id ? updated : v)));
      } else {
        setFilteredData(filteredData.filter((v) => v.id !== updated.id));
      }
    }
  }, [createEtfValueSubscription.data, updateEtfValueSubscription.data]);

  useEffect(() => {
    if (etfData.data && etfData.data.result) {
      setEtf(etfData.data.result);
    }
  }, [etfData]);

  useEffect(() => {
    if (data && data.result) {
      setEtfValues(data.result as IEtfValue[]);
      setFilteredData(data.result as IEtfValue[]);
    }
  }, [data]);

  useEffect(() => {
    let filterByDate = etfValues;
    const currentStartDate = startDate
      ? moment.utc(new Date(startDate.toISOString().split('T')[0]))
      : null;
    const currentEndDate = endDate
      ? moment.utc(new Date(endDate.toISOString().split('T')[0]))
      : null;
    if (currentStartDate) {
      filterByDate = filterByDate.filter((v) =>
        moment.utc(v.date).isSameOrAfter(moment(currentStartDate))
      );
    }
    if (currentEndDate) {
      filterByDate = filterByDate.filter((v) => moment.utc(v.date).isSameOrBefore(currentEndDate));
    }
    setFilteredData(filterByDate);
  }, [startDate, endDate]);

  const columns = [
    { title: 'Details', width: 60, sorting: false },
    {
      title: 'Date',
      width: 118,
      render: (rowData: IEtfValue) => renderDateString(rowData.date)
    },
    { title: 'Provider', width: 177, field: 'etf.provider' },
    { title: 'Name', width: 454, field: 'etf.name' },
    { title: 'Benchmark name', width: 279, field: 'etf.identifier' },
    { title: 'AUM(mln)USD', width: 144, field: 'aum' },
    { title: 'ETF 1yr total return %', width: 164, field: 'total_return_one_year' },
    {
      title: 'Index 1yr total return %',
      width: 164,
      field: 'index_total_return_one_year'
    },
    { title: 'Etf 3yr total return %', width: 164, field: 'total_return_three_year' },
    { title: 'Index 3yr total return %', width: 164, field: 'index_total_return_three_year' }
  ];

  const handleToEtfValuesOverview = () => {
    navigate(AuthRoutes.Etfs, {
      state: { ...(locationState || 0) }
    });
  };

  const handleDetails = (rowData: IEtfValue) => {
    setEtfValue(rowData);
    setOpenForm(true);
  };

  const handleClose = (saved: boolean, isNew?: boolean, etfValue?: IEtfValue) => {
    if (saved && isNew) {
      addEtfValueDocument(etfValue as IEtfValue);
    }

    setEtfValue(null);
    setOpenForm(false);
  };

  const addEtfValueDocument = (etfValue: IEtfValue) => {
    const existing = etfValues.find((v) => v.id === etfValue.id);
    if (!existing) {
      setEtfValues([etfValue, ...etfValues]);
    }
    let addToFiltered = true;
    if (startDate) {
      const momentStart = moment.utc(startDate.toISOString().split('T')[0]);
      addToFiltered = moment.utc(etfValue.date).isSameOrAfter(moment(momentStart));
    }
    if (addToFiltered && endDate) {
      const momentEnd = moment.utc(endDate.toISOString().split('T')[0]);
      addToFiltered = moment.utc(etfValue.date).isSameOrBefore(moment(momentEnd));
    }
    if (addToFiltered) {
      setFilteredData([etfValue, ...filteredData]);
    }
  };

  const handleExport = () => {
    const fileName = `${locationState?.name || 'ETF Values'}${
      startDate ? ` from ${moment(startDate).format('YYYY_MM_DD')}` : ''
    }${endDate ? ` until ${moment(endDate).format('YYYY_MM_DD')}` : ''}`;
    const csvData = filteredData.map((r) => ({
      Date: moment(parseInt(r.date)).format(DATE_TIME_FORMATS.DATE_AND_TIME),
      Provider: r.etf.provider,
      Name: r.etf.name,
      'Benchmark name': r.etf.identifier,
      'AUM(min)USD': r.aum,
      'ETF 1yr total return %': r.total_return_one_year,
      'Index 1yr total return %': r.index_total_return_one_year,
      'Etf 3yr total return %': r.total_return_three_year,
      'Index 3yr total return %': r.index_total_return_three_year
    }));
    exportDataAsExcel(csvData, fileName);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <OverviewToolbar handleToOverview={handleToEtfValuesOverview} navName={RouteNames.EtfValues}>
        {etf?.name || ''}
      </OverviewToolbar>
      <Box className="overviewPage">
        <Box className="print\:w-full">
          <OverviewTable
            columns={columns}
            data={filteredData || []}
            loading={loading}
            handleDetails={handleDetails}
            handleRemove={() => console.log('Hello')}
            pageSize={15}
            createButtonProps={{
              id: 'create-etf-with-values-button',
              onClick: () => setOpenForm(true),
              text: 'Create',
              disabled: false
            }}
            exportButtonProps={{ id: 'export-history-etfs-button', onClick: () => handleExport() }}
            importButtonProps={{ id: 'import-history-etfs-button', onClick: () => true }}
            additionalFilters={
              <Box sx={{ display: 'flex', gap: '15px' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <EpraDatePicker
                    id="start-date-picker"
                    label="Start Date"
                    clearable={true}
                    minDate={minDate}
                    maxDate={endDate || new Date()}
                    value={startDate}
                    onChange={(value) => setStartDate(value)}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <EpraDatePicker
                    id="end-date-picker"
                    label="End Date"
                    clearable={true}
                    minDate={startDate || minDate}
                    maxDate={endDate || new Date()}
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                  />
                </LocalizationProvider>
              </Box>
            }
          />
          {openForm && (
            <EtfValueForm handleClose={handleClose} etf={etf as IEtf} etfValueInput={etfValue} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EtfHistory;
